const ANALYSIS_TARGET_PROJECT_TYPES = [
  "SearchAnalyticsProject",
  "UserAnalyticsProject",
  "IndexWorkerProject",
] as const;

type AnalysisTargetProjectType = (typeof ANALYSIS_TARGET_PROJECT_TYPES)[number];

export type AnalysisTarget = {
  projectId: string;
  projectName: string;
  projectType: AnalysisTargetProjectType;
  iwId?: string;
  iwName?: string;
  viewId?: string;
  viewName?: string;
};

export function getAnalysisTargetKey(target: AnalysisTarget): string {
  if (target.iwId) {
    return `iw:${target.iwId}`;
  }
  if (target.viewId) {
    return `view:${target.viewId}`;
  }
  return `project:${target.projectId}`;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function parseRequiredString(
  record: Record<string, unknown>,
  key: string,
): string {
  const value = record[key];
  if (!isNonEmptyString(value)) {
    throw new Error(`analysisTarget.${key} must be a non-empty string.`);
  }
  return value;
}

function parseOptionalString(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];
  if (value === null || value === undefined) {
    return undefined;
  }
  if (!isNonEmptyString(value)) {
    throw new Error(
      `analysisTarget.${key} must be omitted or a non-empty string.`,
    );
  }
  return value;
}

function parseProjectType(raw: unknown): AnalysisTargetProjectType {
  if (
    typeof raw !== "string" ||
    !ANALYSIS_TARGET_PROJECT_TYPES.includes(raw as AnalysisTargetProjectType)
  ) {
    throw new Error(
      "analysisTarget.projectType must be one of SearchAnalyticsProject, UserAnalyticsProject, IndexWorkerProject.",
    );
  }
  return raw as AnalysisTargetProjectType;
}

/**
 * AI チャットで使う分析対象コンテキストを厳密に検証して正規化する。
 *
 * 契約:
 * - `null`/`undefined` は「未選択」として `null` を返す。
 * - `iwId` と `iwName` は常に同時に存在する（片方のみは不正）。
 * - `viewId` と `viewName` は常に同時に存在する（片方のみは不正）。
 * - `iw*` と `view*` の同時指定は許可しない。
 * - Index Worker プロジェクトには `iw*` のみ許可し、SA/UA には `view*` のみ許可する。
 *
 * 失敗時は例外を投げ、入力不整合を呼び出し元で可視化できるようにする。
 */
export function parseAnalysisTarget(raw: unknown): AnalysisTarget | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  if (typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("analysisTarget must be an object.");
  }

  const record = raw as Record<string, unknown>;
  const projectId = parseRequiredString(record, "projectId");
  const projectName = parseRequiredString(record, "projectName");
  const projectType = parseProjectType(record.projectType);
  const iwId = parseOptionalString(record, "iwId");
  const iwName = parseOptionalString(record, "iwName");
  const viewId = parseOptionalString(record, "viewId");
  const viewName = parseOptionalString(record, "viewName");
  const hasIwPair = Boolean(iwId) || Boolean(iwName);
  const hasViewPair = Boolean(viewId) || Boolean(viewName);

  if (Boolean(iwId) !== Boolean(iwName)) {
    throw new Error(
      "analysisTarget.iwId and analysisTarget.iwName must be provided together.",
    );
  }
  if (Boolean(viewId) !== Boolean(viewName)) {
    throw new Error(
      "analysisTarget.viewId and analysisTarget.viewName must be provided together.",
    );
  }
  if (hasIwPair && hasViewPair) {
    throw new Error(
      "analysisTarget cannot include both index worker detail and analytics view detail.",
    );
  }
  if (projectType === "IndexWorkerProject" && hasViewPair) {
    throw new Error(
      "analysisTarget for IndexWorkerProject cannot include view.",
    );
  }
  if (projectType !== "IndexWorkerProject" && hasIwPair) {
    throw new Error(
      "analysisTarget.iwId is only allowed for IndexWorkerProject.",
    );
  }

  return {
    projectId,
    projectName,
    projectType,
    ...(iwId ? { iwId } : {}),
    ...(iwName ? { iwName } : {}),
    ...(viewId ? { viewId } : {}),
    ...(viewName ? { viewName } : {}),
  };
}

/**
 * 複数の analysisTarget 配列を検証し、重複を除いた正規化配列を返す。
 *
 * 契約:
 * - `null`/`undefined` は空配列扱い。
 * - 各要素は `parseAnalysisTarget` と同じ厳格検証を通す。
 * - 重複は先勝ち（最初に現れた要素を保持）。
 */
export function parseAnalysisTargets(raw: unknown): AnalysisTarget[] {
  if (raw === null || raw === undefined) {
    return [];
  }
  if (!Array.isArray(raw)) {
    throw new Error("analysisTargets must be an array.");
  }

  const normalizedTargets: AnalysisTarget[] = [];
  const seen = new Set<string>();

  raw.forEach((candidate, index) => {
    const parsed = parseAnalysisTarget(candidate);
    if (!parsed) {
      throw new Error(
        `analysisTargets[${index}] must be a valid analysisTarget object.`,
      );
    }
    const key = getAnalysisTargetKey(parsed);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    normalizedTargets.push(parsed);
  });

  return normalizedTargets;
}

export function formatAnalysisTargetLabel(target: AnalysisTarget): string {
  if (target.iwName) {
    return `${target.projectName} / ${target.iwName}`;
  }
  if (target.viewName) {
    return `${target.projectName} / ${target.viewName}`;
  }
  return target.projectName;
}
